import { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper, { Area, MediaSize, Size } from 'react-easy-crop';

import { Modal, Slider, Spinner, Tabs } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';

import { Controls, CropArea, WHITE_BACKGROUND, ZoomSlider } from './customize-img-modal.styled';
import { detectImageBackground, type ImageBackground } from './detect-image-background';

export type CustomizeImgModalProps = {
  inputFile?: File;
  outputWidth?: number;
  outputHeight?: number;
  aspect?: number;
  cropSize?: Size;
  backgroundChoice?: boolean;
  onSave?: (_file: File) => void | Promise<void>;
};

const MAX_FRAME: Size = { width: 800, height: 500 };

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 4;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type = 'image/png', quality = 1) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality);
  });

const stripExt = (name: string) => name.replace(/\.[^.]+$/, '');

const fitCropSize = (maxW: number, maxH: number, aspect: number): Size => {
  let w = maxW;
  let h = w / aspect;

  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }

  return { width: Math.round(w), height: Math.round(h) };
};

type SrcRect = { sx: number; sy: number; sw: number; sh: number };

const drawWithIntersection = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  srcRect: SrcRect,
  outW: number,
  outH: number,
) => {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const { sx, sy, sw, sh } = srcRect;
  const ix0 = Math.max(0, sx);
  const iy0 = Math.max(0, sy);
  const ix1 = Math.min(iw, sx + sw);
  const iy1 = Math.min(ih, sy + sh);
  const iW = ix1 - ix0;
  const iH = iy1 - iy0;
  if (iW <= 0 || iH <= 0) return;
  const dx = ((ix0 - sx) / sw) * outW;
  const dy = ((iy0 - sy) / sh) * outH;
  const dW = (iW / sw) * outW;
  const dH = (iH / sh) * outH;

  ctx.drawImage(
    img,
    Math.round(ix0),
    Math.round(iy0),
    Math.round(iW),
    Math.round(iH),
    dx,
    dy,
    dW,
    dH,
  );
};

export const CustomizeImgModal = () => {
  const { handleClose, open, props } = useGlobalModal('CUSTOMIZE_IMG_MODAL');

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState<Area | null>(null);
  const [frameSize, setFrameSize] = useState<Size | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [background, setBackground] = useState<ImageBackground | null>(null);

  const outW = props?.outputWidth ?? 388;
  const outH = props?.outputHeight ?? 388;

  const lockedAspect = props?.aspect ?? null;
  const contractAspect = lockedAspect ?? outW / outH;
  const defaultFrame: Size =
    props?.cropSize ?? fitCropSize(MAX_FRAME.width, MAX_FRAME.height, contractAspect);
  const uiCropSize = lockedAspect ? props?.cropSize : (frameSize ?? defaultFrame);
  const frameAspect =
    lockedAspect ?? (uiCropSize ? uiCropSize.width / uiCropSize.height : contractAspect);

  const src = useMemo(() => {
    if (!props?.inputFile) return '';
    return URL.createObjectURL(props.inputFile);
  }, [props?.inputFile]);

  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src);
    };
  }, [src]);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedPixels(null);
    setFrameSize(null);
    setIsSaving(false);
    setBackground(null);
  }, [open]);

  useEffect(() => {
    if (!open || !src || !props?.backgroundChoice) return;
    let cancelled = false;
    loadImage(src)
      .then((img) => {
        if (!cancelled) setBackground(detectImageBackground(img));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, src, props?.backgroundChoice]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedPixels(pixels);
  }, []);

  const onMediaLoaded = useCallback(
    (mediaSize: MediaSize) => {
      if (lockedAspect) return;
      setFrameSize({ width: Math.round(mediaSize.width), height: Math.round(mediaSize.height) });
    },
    [lockedAspect],
  );

  const handleSave = useCallback(async () => {
    if (!props?.onSave || !props?.inputFile || !src || !croppedPixels || isSaving) return;

    setIsSaving(true);
    try {
      const img = await loadImage(src);

      const exportSize = fitCropSize(outW, outH, frameAspect);

      const canvas = document.createElement('canvas');
      canvas.width = exportSize.width;
      canvas.height = exportSize.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      if (background === 'white') {
        ctx.fillStyle = WHITE_BACKGROUND;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      drawWithIntersection(
        ctx,
        img,
        {
          sx: croppedPixels.x,
          sy: croppedPixels.y,
          sw: croppedPixels.width,
          sh: croppedPixels.height,
        },
        exportSize.width,
        exportSize.height,
      );

      const blob = await canvasToBlob(canvas, 'image/png', 1);

      const baseName = stripExt(props.inputFile.name || 'image');
      const newFile = new File([blob], `${baseName}.png`, { type: 'image/png' });

      await props.onSave(newFile);
      handleClose();
    } finally {
      setIsSaving(false);
    }
  }, [background, croppedPixels, frameAspect, handleClose, isSaving, outW, outH, props, src]);

  const canSave = !!props?.inputFile && !!croppedPixels && !isSaving;

  const handleOpenChange = (next: boolean) => {
    if (!next && !isSaving) handleClose();
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="Customize image"
      showClose
      maxWidth={880}
      maxHeight="90dvh"
      primaryAction={{
        label: 'Save',
        icon: isSaving ? <Spinner color="primary" /> : undefined,
        onClick: handleSave,
        disabled: !canSave,
      }}
      secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: isSaving }}
    >
      <CropArea $white={background === 'white'}>
        {!!src && (
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            aspect={frameAspect}
            cropSize={uiCropSize}
            objectFit={lockedAspect ? 'cover' : 'contain'}
            restrictPosition={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={onMediaLoaded}
          />
        )}
      </CropArea>

      <Controls>
        {background !== null && (
          <Tabs.Root
            value={background}
            onValueChange={(next) => setBackground(next as ImageBackground)}
            variant="boxed"
            size="sm"
          >
            <Tabs.List>
              <Tabs.Tab value="white" label="White background" />
              <Tabs.Tab value="transparent" label="Transparent" />
            </Tabs.List>
          </Tabs.Root>
        )}
        <ZoomSlider>
          <Slider
            label="Zoom"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onValueChange={(value) => setZoom(Array.isArray(value) ? value[0] : value)}
            disabled={isSaving}
          />
        </ZoomSlider>
      </Controls>
    </Modal>
  );
};
